import { CoreButton, CoreCheckbox, CoreInput, CoreSelect, CoreSteps } from '@components'
import BankMetadataFieldInput from '@recipients/components/BankMetadataFieldInput'
import { formatCurrencyForDisplay } from '@utils'
import {
  CurrencyTypes,
  useCountries,
  useCurrencies,
  useEditBankAccountQry,
  useMerchantQry,
  useWithdrawRecipientsAndCountries,
  WithdrawalSubFlows
} from 'mmfintech-backend-api'
import type { BankFieldMetadata, Currency, WithdrawalSubFlow } from 'mmfintech-backend-api/types'
import { isValidArray, tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useEffect, useMemo } from 'react'
import { BANK_RECIPIENT_ERROR_SUBSTITUTES } from '../constants'

import './BankRecipientModal.scss'

interface Props {
  recipientId?: number
  accountId?: number
  currencyCode?: string
  foreignId?: string
  personal?: boolean
  onSuccess?: (response: any) => void
  isCurrencyEditable?: boolean
  independentEdit?: boolean
  initialError?: unknown
  subFlow?: WithdrawalSubFlow
}

function BankRecipientModal(props: Props) {
  const {
    recipientId,
    accountId,
    currencyCode,
    foreignId,
    personal = false,
    onSuccess,
    isCurrencyEditable = false,
    independentEdit = true,
    initialError,
    subFlow = WithdrawalSubFlows.BANKWIRE
  } = props

  const { isMerchantCompany } = useMerchantQry()

  const { currencyOptions } = useCurrencies()
  const { countryOptions, countryOptionsFiltered } = useCountries('WITHDRAWAL')

  const { countries } = useWithdrawRecipientsAndCountries({
    subFlow,
    accountId: accountId
  })

  const filteredCountries = useMemo(
    () => countryOptionsFiltered('WITHDRAWAL', countries),
    [countryOptionsFiltered, countries]
  )

  const {
    step,
    setStep,
    formValues,
    countryCode,
    setCountryCode,
    bankMetadata,
    bankMetadataError,
    bankMetadataFetching,
    accountTypeOptions,
    documentTypes,
    documentTypesOptions,
    handleSubmit,
    providedBanks,
    isFieldVisible,
    saveRecipientError,
    saveRecipientLoading
  } = useEditBankAccountQry({
    accountId,
    recipientId,
    currencyCode,
    enableProvidedBanks: true,
    foreignId: !personal ? foreignId : undefined,
    // Only the Mastercard tab needs provider-specific metadata; other tabs keep the default (bankwire) response
    subFlow: subFlow === WithdrawalSubFlows.MASTERCARD ? subFlow : undefined,

    onSuccess: (response: any) => {
      if (isCurrencyEditable) {
        onSuccess({ ...response, currency: formValues.getValue('currency') })
      } else {
        onSuccess(response)
      }
    }
  })

  const metadataCustomError = useMemo(() => {
    if (bankMetadataError && formValues.getValue('currency') && countryCode) {
      return {
        error: `${tr(
          'FRONTEND.RECIPIENTS.METADATA.CUSTOM_ERROR_WITHDRAW',
          'Cannot withdraw {{CURRENCY}} in {{COUNTRY}}',
          {
            CURRENCY: formatCurrencyForDisplay(formValues.getValue('currency')),
            COUNTRY: countryOptions?.find(x => x.value === countryCode)?.label
          }
        )}`
      }
    }
    return null
  }, [bankMetadataError])

  const validMetadataCategory = (field: BankFieldMetadata, category: string): boolean =>
    field.code !== 'COUNTRY_CODE' && field.code !== 'HOLDER_NAME' && field.fieldCategory === category

  const hasSecondStep = bankMetadata?.some(meta => validMetadataCategory(meta, 'RECIPIENT_DETAILS'))

  const handleClick = () => {
    formValues.setValue('personal', !formValues.getValue('personal'))
  }

  const handleContinue = () => {
    if (formValues.areValid(1)) {
      setStep(2)
    }
  }

  useEffect(() => {
    formValues.setValue('personal', personal)
  }, [personal])

  useEffect(() => {
    if (saveRecipientError) {
      if ((saveRecipientError as any)?.errorKey.includes('BANK_ACCOUNT_DATA_NOT_SUFFICIENT')) {
        setStep(1)
      }
      formValues.handleErrors(saveRecipientError, BANK_RECIPIENT_ERROR_SUBSTITUTES)
    }
  }, [saveRecipientError])

  useEffect(() => {
    if (isValidArray(documentTypes)) {
      const { nationalId } = documentTypes[0]
      formValues.setValue('documentType', nationalId)
    }
  }, [documentTypes])

  useEffect(() => {
    if (initialError && isValidArray(bankMetadata)) {
      formValues.handleErrors(initialError, BANK_RECIPIENT_ERROR_SUBSTITUTES)
    }
  }, [bankMetadata, initialError])

  return (
    <div className='recipient-edit-wrapper' data-test='bank-recipient-edit-modal'>
      {!independentEdit && recipientId > 0 && (
        <div className='subtitle'>
          {tr(
            'FRONTEND.RECIPIENTS.EDIT.SUBTITLE',
            'Please note that in order to proceed with the payment execution additional information about the recipient is required.'
          )}
        </div>
      )}

      {hasSecondStep && (
        <div className='recipient-edit-steps'>
          <CoreSteps
            step={step}
            maxStep={2}
            title={
              step === 1
                ? tr('FRONTEND.CUSTOMERS.EDIT.TITLE_PAYMENT', 'Payment information')
                : tr('FRONTEND.CUSTOMERS.EDIT.TITLE_PERSONAL', 'Personal information')
            }
          />
        </div>
      )}

      <div className='form-elements-container'>
        {step === 1 ? (
          <>
            {accountId ? (
              (!currencyCode || isCurrencyEditable) && (
                <CoreSelect
                  type='currency'
                  data-test='currency'
                  label={tr('FRONTEND.ACCOUNTS.CREATE.CURRENCY_LABEL', 'Currency')}
                  options={currencyOptions((c: Currency) => c.supportedForWithdrawal)}
                  {...formValues.registerInput('currency')}
                />
              )
            ) : (
              <CoreSelect
                type='currency'
                data-test='currency'
                label={tr('FRONTEND.ACCOUNTS.CREATE.CURRENCY_LABEL', 'Currency')}
                options={currencyOptions((c: Currency) => c.type === CurrencyTypes.FIAT)}
                value={formValues.getValue('currency')}
                onChange={(_, val: any) => {
                  formValues.setValue('currency', val.value)
                }}
              />
            )}

            <CoreSelect
              type='country'
              name='country'
              key='country'
              value={countryCode}
              options={filteredCountries}
              data-test='country-code'
              label={tr('FRONTEND.RECIPIENTS.LABELS.BANK_COUNTRY', 'Country')}
              onChange={(_: string, value: string) => setCountryCode(value)}
              required
            />

            <CoreInput
              type='text'
              data-test='account-name'
              maxLength={100}
              label={tr('FRONTEND.RECIPIENTS.LABELS.ACCOUNT_NAME', 'Recipient name')}
              hint={
                subFlow === WithdrawalSubFlows.MASTERCARD
                  ? tr(
                      'FRONTEND.RECIPIENTS.BANK.NAME_WARNING',
                      'Beneficiary name must exactly match the name registered with their bank.'
                    )
                  : undefined
              }
              {...formValues.registerInput('name')}
              autoComplete='off'
            />

            {isValidArray(bankMetadata) &&
              formValues &&
              bankMetadata
                .filter(meta => validMetadataCategory(meta, 'BANK_DETAILS') && isFieldVisible(meta))
                .map(field => (
                  <BankMetadataFieldInput
                    key={field.code}
                    field={field}
                    formValues={formValues}
                    countryCode={countryCode}
                    accountTypes={accountTypeOptions}
                    providedBanks={providedBanks}
                    documentTypesOptions={documentTypesOptions}
                  />
                ))}

            <CoreInput
              type='text'
              maxLength={100}
              data-test='description'
              label={tr('FRONTEND.RECIPIENTS.LABELS.DESCRIPTION', 'Description')}
              {...formValues.registerInput('description')}
              autoComplete='off'
            />

            {isMerchantCompany && (
              <CoreCheckbox
                className='form-checkbox'
                type='check'
                checked={Boolean(formValues.getValue('personal'))}
                disabled={formValues.getValue('foreignId') || personal}
                value={'personal'}
                onClick={handleClick}
                label={tr('FRONTEND.RECIPIENTS.LABELS.BANK_PERSONAL_ACCOUNT', 'This is my personal account')}
                {...formValues.registerCheckbox('personal')}
                tooltip={{
                  enabled: true,
                  text: tr(
                    'FRONTEND.RECIPIENTS.LABELS.CRYPTO_PERSONAL_ACCOUNT_TOOLTIP',
                    'Personal accounts are your own external accounts and are only visible to you, administrators, and authorized departments within a company account.'
                  ),
                  place: 'bottom',
                  padding: '3px',
                  backgroundColor: 'black',
                  textColor: 'white'
                }}
              />
            )}
          </>
        ) : (
          isValidArray(bankMetadata) &&
          bankMetadata
            .filter(meta => validMetadataCategory(meta, 'RECIPIENT_DETAILS') && isFieldVisible(meta))
            .map(field => (
              <BankMetadataFieldInput
                key={field.code}
                field={field}
                formValues={formValues}
                countryCode={countryCode}
                accountTypes={accountTypeOptions}
                providedBanks={providedBanks}
                documentTypesOptions={documentTypesOptions}
              />
            ))
        )}
      </div>
      <ErrorDisplay error={[saveRecipientError, metadataCustomError]} />

      <div className='recipient-edit-actions account'>
        <div className='buttons'>
          {step > 1 && (
            <CoreButton
              variation='tertiary'
              type='button'
              text={tr('FRONTEND.BUTTONS.BACK', 'Back')}
              data-test='button-back'
              disabled={bankMetadataFetching}
              onClick={() => setStep(step - 1)}
              size='large'
            />
          )}

          {hasSecondStep && step === 1 ? (
            <CoreButton
              type='button'
              text={tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')}
              data-test='button-continue'
              disabled={bankMetadataFetching}
              onClick={handleContinue}
              fullWidth
              variation='primary-black'
              size='large'
            />
          ) : (
            <CoreButton
              type='button'
              className='primary'
              variation='primary-black'
              text={tr('FRONTEND.RECIPIENTS.EDIT.BUTTON_SAVE', 'Save')}
              isLoading={saveRecipientLoading}
              disabled={bankMetadataFetching || !!metadataCustomError}
              onClick={() => void handleSubmit()}
              data-test='button-submit'
              size='large'
              fullWidth
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default BankRecipientModal
